import goToTpf from '@/functions/goToTpf'

const PUBLIC_ROUTES = ['/', '/home', '/news', '/fixtures']

const SUBSCRIPTION_ID_PARAMS = ['subscription-id', 'subscriptionId']

const TPF_HOSTED_ROUTES = ['Subscribe', 'OTPVerification', 'Onboarding']

let tealiumInjectQueued = false

function firstQueryValue(value) {
  return Array.isArray(value) ? value[0] : value
}

function getSubscriptionIdFromQuery(query) {
  for (const key of SUBSCRIPTION_ID_PARAMS) {
    const value = firstQueryValue(query?.[key])
    if (value) return value
  }
  return undefined
}

function stripQueryParamsFromUrl(keys) {
  const url = new URL(window.location.href)
  const removed = keys.filter((key) => url.searchParams.has(key))
  if (!removed.length) return
  removed.forEach((key) => url.searchParams.delete(key))
  history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`)
}

function maybeQueueTealiumInject(store) {
  if (tealiumInjectQueued) return
  if (!store.state.brandConfig?.brand?.includes('vc')) return
  tealiumInjectQueued = true
  const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 2500))
  schedule(async () => {
    try {
      const { injectScript } = await import('@/services/tealium')
      await injectScript()
    } catch (e) {
      // Analytics failure must not affect navigation.
    }
  })
}

export default async function userAuthenticate(to, from, next, store) {
  maybeQueueTealiumInject(store)

  const subscriptionId = getSubscriptionIdFromQuery(to.query)
  const eMsisdn = firstQueryValue(to.query?.['e-msisdn'])
  const hasIdentityParam = Boolean(subscriptionId || eMsisdn)

  if (subscriptionId) {
    try {
      await store.dispatch('loginWithSubscriptionId', subscriptionId)
    } catch (err) {
      console.error('subscription-id login failed:', err)
    }
    stripQueryParamsFromUrl(SUBSCRIPTION_ID_PARAMS)
  } else if (eMsisdn) {
    // Handle e-msisdn login from URL params (e.g. redirect from voucher store)
    try {
      await store.dispatch('loginWithEmsisdn', eMsisdn)
    } catch (err) {
      console.error('e-msisdn login failed:', err)
    }
    // Always remove e-msisdn from URL (on success or failure)
    stripQueryParamsFromUrl(['e-msisdn'])
  }

  if (!hasIdentityParam && TPF_HOSTED_ROUTES.includes(to.name)) {
    goToTpf()
    return next(false)
  }

  const msisdn = localStorage.getItem('msisdn')
  const sessionId = localStorage.getItem('sessionId')
  const isPublicRoute = PUBLIC_ROUTES.includes(to.path)
  const requiresAuth = to.meta?.requiresAuth === true

  if (!sessionId || !msisdn) {
    if (requiresAuth) {
      store.commit('setIntendedPath', to.fullPath)
      goToTpf()
      return next(false)
    }
    return isPublicRoute ? next() : next('/home')
  }

  if (!store.state.user) {
    try {
      store.commit('setMsisdn', msisdn)
      store.commit('setSessionId', sessionId)

      const user = await store.dispatch('fetchAndSetUser', { force: false })

      if (!user) {
        await store.dispatch('clearSession')
        return next('/home')
      }
    } catch (error) {
      console.error('Authentication error:', error)
      await store.dispatch('clearSession')
      return next('/home')
    }
  }

  if (to.meta?.requiresSubscription && !store.getters.isSubscribed) {
    goToTpf()
    return next(false)
  }

  next()
}
